import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { cn } from './cn';

/** The scroll container of the screen on top, for the tab bar's scroll-to-top. */
export const SCREEN_SCROLL_ID = 'screen-scroll';

/** Height of the large title block; the scroll offset where the bar takes over. */
const TITLE_H = 52;

/**
 * One screen: a nav bar, then ONE scroll container holding the large title and
 * the body. The document itself never scrolls (index.css), so there is exactly
 * one thing that moves under a finger, as in a native app.
 *
 * The nav bar is solid page colour. Once the large title has scrolled under
 * it, it gets a hairline and the small centered title. A scroll that stops
 * with the title half under the bar snaps to one state or the other, as UIKit
 * does, so a title is never left cut in half.
 *
 * No pull to refresh: data refetches when the app comes back to the
 * foreground (main.tsx) and after every edit.
 */
export function Screen({
  title,
  back,
  backLabel,
  actions,
  navCenter,
  large = true,
  children,
  className
}: {
  title: string;
  back?: string;
  backLabel?: string;
  actions?: ReactNode;
  /** Replaces the collapsed title in the nav bar (e.g. "Oct 5 – 11" with arrows). */
  navCenter?: ReactNode;
  large?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let settle: ReturnType<typeof setTimeout> | null = null;
    let touching = false;

    const snap = () => {
      if (!large || touching) return;
      const top = el.scrollTop;
      if (top > 0 && top < TITLE_H) el.scrollTo({ top: top < TITLE_H / 2 ? 0 : TITLE_H, behavior: 'smooth' });
    };
    const later = () => {
      if (settle) clearTimeout(settle);
      settle = setTimeout(snap, 140);
    };
    const onScroll = () => {
      setScrolled(el.scrollTop > (large ? TITLE_H - 8 : 2));
      later();
    };
    const onStart = () => {
      touching = true;
    };
    const onEnd = () => {
      touching = false;
      later();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      if (settle) clearTimeout(settle);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [large]);

  const collapsed = !large || scrolled;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header
        className={cn(
          'relative z-30 shrink-0 bg-[var(--page)] pt-[var(--sat)]',
          'after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[var(--line)] after:transition-opacity',
          collapsed ? 'after:opacity-100' : 'after:opacity-0'
        )}
      >
        <div className="relative flex h-11 items-center px-2">
          {back !== undefined && (
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(back))}
              className="relative z-10 flex h-11 items-center gap-0.5 pr-3 text-body text-[var(--accent)] active:opacity-50"
            >
              <ChevronLeft className="size-[22px]" strokeWidth={2.2} />
              <span className="max-w-[8rem] truncate">{backLabel ?? 'Back'}</span>
            </button>
          )}
          {navCenter ? (
            <div className="absolute inset-x-0 flex justify-center">{navCenter}</div>
          ) : (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-28 truncate text-center text-body font-semibold transition-opacity duration-150',
                collapsed ? 'opacity-100' : 'opacity-0'
              )}
            >
              {title}
            </div>
          )}
          <div className="relative z-10 ml-auto flex items-center">{actions}</div>
        </div>
      </header>

      <div ref={scroller} id={SCREEN_SCROLL_ID} className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        {large && (
          <h1 className="px-4 text-largetitle font-semibold tracking-[-0.02em]" style={{ height: TITLE_H }}>
            {title}
          </h1>
        )}
        <main className={cn('flex flex-col gap-4 px-4 pb-6', !large && 'pt-4', className)}>{children}</main>
      </div>
    </div>
  );
}
